import { useCallback, useEffect, useMemo, useRef } from 'react'
import { z } from 'zod'

interface SchemaInputProps {
  schema: z.ZodSchema<any>
  defaultValue?: any
  hasDefault: boolean
  onChange?: (value: any) => void
  readOnly?: boolean
}

// Helper function to get the Zod schema type
export const getZodSchemaType = (schema: z.ZodSchema<any>): string => {
  const typeName = (schema._def as any)?.typeName
  if (typeName === 'ZodString') return 'string'
  if (typeName === 'ZodNumber') return 'number'
  if (typeName === 'ZodBoolean') return 'boolean'
  if (typeName === 'ZodArray') return 'array'
  if (typeName === 'ZodObject') return 'object'
  if (typeName === 'ZodNull') return 'null'
  if (typeName === 'ZodUndefined') return 'undefined'
  if (typeName === 'ZodEnum') return 'enum'
  if (typeName === 'ZodUnion') return 'union'
  if (typeName === 'ZodIntersection') return 'intersection'
  if (typeName === 'ZodOptional') return 'optional'
  if (typeName === 'ZodNullable') return 'nullable'
  if (typeName === 'ZodDefault') return 'default'
  if (typeName === 'ZodLiteral') return 'literal'
  if (typeName === 'ZodNativeEnum') return 'native-enum'
  if (typeName === 'ZodDate') return 'date'
  if (typeName === 'ZodBigInt') return 'bigint'
  if (typeName === 'ZodSymbol') return 'symbol'
  if (typeName === 'ZodFunction') return 'function'
  if (typeName === 'ZodAny') return 'any'
  if (typeName === 'ZodUnknown') return 'unknown'
  if (typeName === 'ZodNever') return 'never'
  if (typeName === 'ZodVoid') return 'void'
  if (typeName === 'ZodLazy') return 'lazy'
  if (typeName === 'ZodEffects') return 'effects'
  if (typeName === 'ZodCatch') return 'catch'
  if (typeName === 'ZodBranded') return 'branded'
  if (typeName === 'ZodPipeline') return 'pipeline'
  if (typeName === 'ZodReadonly') return 'readonly'
  if (typeName === 'ZodSet') return 'set'
  if (typeName === 'ZodMap') return 'map'
  if (typeName === 'ZodRecord') return 'record'
  if (typeName === 'ZodTuple') return 'tuple'
  if (typeName === 'ZodDiscriminatedUnion') return 'discriminated-union'
  return 'unknown'
}

// Helper function to get enum values from Zod schema
export const getZodEnumValues = (schema: z.ZodSchema<any>): any[] | undefined => {
  const typeName = (schema._def as any)?.typeName
  if (typeName === 'ZodEnum') {
    // For Zod 3, try to access the enum values directly
    try {
      // The enum values should be in the _def.values property
      return (schema as any)._def?.values || undefined
    } catch (error) {
      console.error('Error getting enum values:', error)
      return undefined
    }
  }
  if (typeName === 'ZodNativeEnum') {
    return Object.values((schema as any).enum)
  }
  if (typeName === 'ZodUnion') {
    // For union of literals, extract the literal values
    const options: any[] = []
    ;(schema as any).options.forEach((option: z.ZodSchema<any>) => {
      if ((option._def as any)?.typeName === 'ZodLiteral') {
        options.push((option as any).value)
      }
    })
    return options.length > 0 ? options : undefined
  }
  return undefined
}

// Helper function to get literal value from Zod schema
export const getZodLiteralValue = (schema: z.ZodSchema<any>): any => {
  if ((schema._def as any)?.typeName === 'ZodLiteral') {
    return (schema as any).value
  }
  return undefined
}

// Helper function to check if schema is nullable (anywhere in the chain)
export const isZodNullable = (schema: z.ZodSchema<any>): boolean => {
  let currentSchema = schema

  // Check the entire chain for nullable types
  while (true) {
    const typeName = (currentSchema._def as any)?.typeName

    if (typeName === 'ZodNullable') {
      return true
    } else if (typeName === 'ZodOptional') {
      // Continue checking deeper - safely unwrap
      if (typeof (currentSchema as any).unwrap === 'function') {
        currentSchema = (currentSchema as any).unwrap()
      } else {
        break
      }
    } else if (typeName === 'ZodDefault') {
      // Continue checking deeper - safely unwrap
      if (typeof (currentSchema as any).removeDefault === 'function') {
        currentSchema = (currentSchema as any).removeDefault()
      } else {
        break
      }
    } else {
      // No more wrappers to check
      break
    }
  }

  return false
}

// Helper function to get the inner schema (for optional/nullable)
export const getZodInnerSchema = (schema: z.ZodSchema<any>): z.ZodSchema<any> => {
  let currentSchema = schema

  // Recursively unwrap optional, nullable, and default wrappers
  while (true) {
    const typeName = (currentSchema._def as any)?.typeName

    if (typeName === 'ZodOptional') {
      if (typeof (currentSchema as any).unwrap === 'function') {
        currentSchema = (currentSchema as any).unwrap()
      } else {
        break
      }
    } else if (typeName === 'ZodNullable') {
      // Treat nullable as optional - unwrap to get the underlying type
      if (typeof (currentSchema as any).unwrap === 'function') {
        currentSchema = (currentSchema as any).unwrap()
      } else {
        break
      }
    } else if (typeName === 'ZodDefault') {
      if (typeof (currentSchema as any).removeDefault === 'function') {
        currentSchema = (currentSchema as any).removeDefault()
      } else {
        break
      }
    } else {
      // No more wrappers to unwrap
      break
    }
  }

  return currentSchema
}

// Helper function to detect if schema is a string with specific format
export const getZodStringFormat = (schema: z.ZodSchema<any>): string | undefined => {
  if ((schema._def as any)?.typeName === 'ZodString') {
    // Check for common string formats based on validation
    const checks = (schema as any)._def.checks || []
    for (const check of checks) {
      if (check.kind === 'email') return 'email'
      if (check.kind === 'url') return 'url'
      if (check.kind === 'uuid') return 'uuid'
      if (check.kind === 'cuid') return 'cuid'
      if (check.kind === 'cuid2') return 'cuid2'
      if (check.kind === 'ulid') return 'ulid'
      if (check.kind === 'datetime') return 'date-time'
      if (check.kind === 'date') return 'date'
      if (check.kind === 'time') return 'time'
      if (check.kind === 'duration') return 'duration'
      if (check.kind === 'ip') return 'ip'
      if (check.kind === 'regex') return 'regex'
    }
  }
  return undefined
}

// Helper function to check if schema is a number with integer constraint
export const isZodInteger = (schema: z.ZodSchema<any>): boolean => {
  if ((schema._def as any)?.typeName === 'ZodNumber') {
    const checks = (schema as any)._def.checks || []
    return checks.some((check: any) => check.kind === 'int')
  }
  return false
}

// Helper function to extract Zod default values
const getZodDefaultValue = (schema: z.ZodSchema<any>): any => {
  let currentSchema = schema

  // Check the entire chain for default values
  while (true) {
    const typeName = (currentSchema._def as any)?.typeName

    if (typeName === 'ZodDefault') {
      // Extract the default value from ZodDefault
      return (currentSchema as any)._def.defaultValue()
    } else if (typeName === 'ZodOptional' || typeName === 'ZodNullable') {
      // Continue checking deeper - safely unwrap
      if (typeof (currentSchema as any).unwrap === 'function') {
        currentSchema = (currentSchema as any).unwrap()
      } else {
        break
      }
    } else {
      // No more wrappers to check
      break
    }
  }

  return undefined
}

// Helper for textarea editor - moved outside to prevent recreation
function TextareaEditor({
  value,
  readOnly,
  onChange,
  inputClasses,
}: {
  value: string
  readOnly: boolean
  onChange?: (val: string) => void
  inputClasses: string
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Calculate rows based on content length and line breaks
  const calculateRows = (text: string) => {
    const lineBreaks = (text.match(/\n/g) || []).length
    const lineLength = Math.max(...text.split('\n').map((line) => line.length))
    const estimatedLines = Math.ceil(lineLength / 60) // Assume ~60 chars per line
    return Math.max(3, Math.min(15, lineBreaks + estimatedLines + 1))
  }

  // Memoize rows calculation to prevent unnecessary re-renders
  const rows = useMemo(() => calculateRows(value || ''), [value])

  // Auto-resize textarea when value changes
  useEffect(() => {
    if (textareaRef.current && !readOnly) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [value, readOnly])

  if (readOnly) {
    return (
      <div
        className={inputClasses + ' whitespace-pre-wrap py-1.5'}
        style={{ minHeight: `${rows * 1.5}rem` }}
      >
        {value}
      </div>
    )
  }
  return (
    <textarea
      ref={textareaRef}
      className={inputClasses + ' resize-y overflow-hidden'}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      rows={1}
      style={{ minHeight: '2.5rem' }}
    />
  )
}

export function SchemaInput({
  schema,
  defaultValue,
  hasDefault,
  onChange,
  readOnly,
}: SchemaInputProps) {
  const arrayItems = hasDefault && Array.isArray(defaultValue) ? defaultValue : []

  const baseInputClasses =
    'w-full px-1.5 py-0.5 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 whitespace-pre-wrap'

  // Get the effective schema (unwrap optional/nullable/default)
  const effectiveSchema = getZodInnerSchema(schema)
  const schemaType = getZodSchemaType(effectiveSchema)

  // Get Zod default value if no explicit defaultValue is provided
  const zodDefaultValue = getZodDefaultValue(schema)
  const effectiveDefaultValue = defaultValue !== undefined ? defaultValue : zodDefaultValue
  const hasEffectiveDefault = hasDefault || zodDefaultValue !== undefined

  // Format effective default value for display, if json string
  const stringAsJsonFormatDefaultValue = useMemo(() => {
    if (effectiveDefaultValue !== null && typeof effectiveDefaultValue === 'string') {
      try {
        return JSON.stringify(JSON.parse(effectiveDefaultValue), null, 2)
      } catch {
        // Do nothing
      }
    }
    return effectiveDefaultValue
  }, [effectiveDefaultValue])

  const inputClasses =
    hasEffectiveDefault && !readOnly
      ? `${baseInputClasses} bg-white`
      : `${baseInputClasses} bg-gray-50 text-gray-500`

  // Stabilize the onChange handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue)
    },
    [onChange]
  )

  // Handle literal values
  const literalValue = getZodLiteralValue(effectiveSchema)
  if (literalValue !== undefined) {
    const constValue = String(literalValue)
    if (hasEffectiveDefault && !readOnly) {
      return <input type="text" className={inputClasses} value={constValue} readOnly />
    }
    return <div className={inputClasses + ' py-1.5'}>{constValue}</div>
  }

  // Handle enum values
  const enumValues = getZodEnumValues(effectiveSchema)
  if (enumValues && Array.isArray(enumValues)) {
    if (hasEffectiveDefault && !readOnly) {
      return (
        <select className={inputClasses} defaultValue={effectiveDefaultValue}>
          {enumValues.map((option: any, index: number) => (
            <option key={index} value={String(option)}>
              {String(option)}
            </option>
          ))}
        </select>
      )
    }
    return (
      <div className={inputClasses + ' py-1.5'}>
        {effectiveDefaultValue || String(enumValues[0] || '—')}
      </div>
    )
  }

  // Handle different schema types
  switch (schemaType) {
    case 'string': {
      const format = getZodStringFormat(effectiveSchema)
      if (['date-time', 'date', 'time', 'email', 'url'].includes(format || '')) {
        // Use native input for these formats
        if (format === 'date-time') {
          if (hasEffectiveDefault && !readOnly) {
            return (
              <input
                type="datetime-local"
                className={inputClasses}
                defaultValue={effectiveDefaultValue}
              />
            )
          }
          return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>
        }
        if (format === 'date') {
          if (hasEffectiveDefault && !readOnly) {
            return (
              <input type="date" className={inputClasses} defaultValue={effectiveDefaultValue} />
            )
          }
          return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>
        }
        if (format === 'time') {
          if (hasEffectiveDefault && !readOnly) {
            return (
              <input type="time" className={inputClasses} defaultValue={effectiveDefaultValue} />
            )
          }
          return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>
        }
        if (format === 'email') {
          if (hasEffectiveDefault && !readOnly) {
            return (
              <input type="email" className={inputClasses} defaultValue={effectiveDefaultValue} />
            )
          }
          return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>
        }
        if (format === 'url') {
          if (hasEffectiveDefault && !readOnly) {
            return (
              <input type="url" className={inputClasses} defaultValue={effectiveDefaultValue} />
            )
          }
          return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>
        }
      }
      // Use textarea for regular string input
      if (hasEffectiveDefault) {
        return (
          <TextareaEditor
            value={stringAsJsonFormatDefaultValue}
            readOnly={readOnly || false}
            onChange={handleChange}
            inputClasses={inputClasses}
          />
        )
      }
      return (
        <TextareaEditor
          value={stringAsJsonFormatDefaultValue}
          readOnly={true}
          inputClasses={inputClasses}
        />
      )
    }
    case 'number':
      if (hasEffectiveDefault && !readOnly) {
        const step = isZodInteger(effectiveSchema) ? '1' : 'any'
        return (
          <input
            type="number"
            className={inputClasses}
            defaultValue={effectiveDefaultValue}
            step={step}
            placeholder={'Enter number'}
          />
        )
      }
      return (
        <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue?.toString() || '—'}</div>
      )

    case 'boolean':
      // Always show an interactive checkbox for booleans
      return (
        <div className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-2 focus:ring-blue-500"
            defaultChecked={effectiveDefaultValue}
            disabled={readOnly}
          />
        </div>
      )

    case 'object':
      if (hasEffectiveDefault) {
        // Destructure object properties
        const objectValue = effectiveDefaultValue || {}
        const shape = (effectiveSchema as z.ZodObject<z.ZodRawShape>).shape

        return (
          <div className="space-y-3">
            {Object.entries(shape).map(([propName, propSchema]) => {
              const propType = getZodSchemaType(propSchema as z.ZodType)
              const isNestedObject = propType === 'object'
              const isNestedArray = propType === 'array'

              return (
                <div
                  key={propName}
                  className={`rounded-lg border border-gray-200 bg-gray-50 p-2 ${isNestedObject || isNestedArray ? 'ml-4' : ''}`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{propName}</span>
                    <span className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-500">
                      {isNestedObject
                        ? `object[${Object.keys((propSchema as z.ZodObject<z.ZodRawShape>).shape).length}]`
                        : isNestedArray
                          ? 'array'
                          : propType}
                    </span>
                  </div>
                  <SchemaInput
                    schema={propSchema as z.ZodType}
                    defaultValue={objectValue[propName]}
                    hasDefault={true}
                    readOnly={readOnly}
                    onChange={(value) => {
                      const newValue = { ...objectValue, [propName]: value }
                      onChange?.(newValue)
                    }}
                  />
                </div>
              )
            })}
          </div>
        )
      }
      return (
        <div className={inputClasses + ' py-1.5 font-mono text-xs'}>
          {effectiveDefaultValue ? JSON.stringify(effectiveDefaultValue, null, 2) : '—'}
        </div>
      )

    case 'array':
      if (hasEffectiveDefault) {
        // Get the underlying type of the array
        const elementSchema = (effectiveSchema as any)._def?.type || effectiveSchema
        const elementType = getZodSchemaType(elementSchema)
        const isNestedObject = elementType === 'object'
        const isNestedArray = elementType === 'array'

        // No add/remove/menu buttons, just render items
        return (
          <div className="space-y-3">
            {arrayItems.map((item, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 ${isNestedObject || isNestedArray ? 'ml-4' : ''}`}
              >
                <div className="flex min-w-0 flex-1">
                  <div className="flex-1">
                    {isNestedObject ? (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-2">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-700">Object</span>
                          <span className="rounded bg-blue-200 px-2 py-1 text-xs text-blue-600">
                            object[$
                            {
                              Object.keys((elementSchema as z.ZodObject<z.ZodRawShape>).shape)
                                .length
                            }
                            ]
                          </span>
                        </div>
                        <SchemaInput
                          schema={elementSchema}
                          defaultValue={item}
                          hasDefault={true}
                          readOnly={readOnly}
                        />
                      </div>
                    ) : isNestedArray ? (
                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-2">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-sm font-medium text-purple-700">Array</span>
                          <span className="rounded bg-purple-200 px-2 py-1 text-xs text-purple-600">
                            array
                          </span>
                        </div>
                        <SchemaInput
                          schema={elementSchema}
                          defaultValue={item}
                          hasDefault={true}
                          readOnly={readOnly}
                        />
                      </div>
                    ) : (
                      <SchemaInput
                        schema={elementSchema}
                        defaultValue={item}
                        hasDefault={true}
                        readOnly={readOnly}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }
      return (
        <div className={inputClasses + ' py-1.5 font-mono text-xs'}>
          {effectiveDefaultValue ? JSON.stringify(effectiveDefaultValue, null, 2) : '—'}
        </div>
      )

    case 'null':
      return <div className={inputClasses + ' py-1.5 text-gray-400'}>null</div>

    case 'date':
      if (hasEffectiveDefault && !readOnly) {
        return <input type="date" className={inputClasses} defaultValue={effectiveDefaultValue} />
      }
      return <div className={inputClasses + ' py-1.5'}>{effectiveDefaultValue || '—'}</div>

    case 'any':
    case 'unknown': {
      // For any/unknown types, use textarea as well
      const stringValue =
        typeof effectiveDefaultValue === 'object'
          ? JSON.stringify(effectiveDefaultValue, null, 2)
          : String(effectiveDefaultValue || '')
      if (hasEffectiveDefault) {
        return (
          <TextareaEditor
            value={stringValue}
            readOnly={readOnly || false}
            onChange={handleChange}
            inputClasses={inputClasses}
          />
        )
      }
      return <TextareaEditor value={stringValue} readOnly={true} inputClasses={inputClasses} />
    }

    default:
      // Fallback for unknown types or schemas without type - treat as any
      if (hasEffectiveDefault && !readOnly) {
        const stringValue =
          typeof effectiveDefaultValue === 'object'
            ? JSON.stringify(effectiveDefaultValue, null, 2)
            : String(effectiveDefaultValue || '')

        return (
          <textarea
            className={inputClasses}
            defaultValue={stringValue}
            rows={3}
            placeholder="Enter value..."
          />
        )
      }
      return (
        <div className={inputClasses + ' py-1.5 font-mono text-xs'}>
          {effectiveDefaultValue ? JSON.stringify(effectiveDefaultValue, null, 2) : '—'}
        </div>
      )
  }
}
